import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const CODE_RE = /^[A-Z0-9][A-Z0-9\-_]{1,31}$/;

export function validateSku(f) {
  const err = {};
  const name = f.name.trim().replace(/\s+/g, " ");
  if (!f.brand_id) err.brand_id = "Select a brand";
  if (name.length < 2 || name.length > 200) err.name = "Name must be 2-200 characters";
  if (f.display_code.trim() && !CODE_RE.test(f.display_code.trim().toUpperCase())) err.display_code = "2-32 chars: letters, digits, - or _";
  if (f.mrp_rupees !== "") {
    if (!/^\d+(\.\d{1,2})?$/.test(f.mrp_rupees.trim())) err.mrp_rupees = "Enter a non-negative amount (max 2 decimals)";
    else if (parseFloat(f.mrp_rupees) > 10000000) err.mrp_rupees = "MRP is too large";
  }
  if (f.reward_points !== "") {
    if (!/^\d+$/.test(f.reward_points.trim())) err.reward_points = "Whole number, 0 or more";
    else if (parseInt(f.reward_points, 10) > 100000) err.reward_points = "Max 100000";
  }
  return err;
}

const Field = ({ label, k, form, set, err, ...rest }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600">{label}</label>
    <input data-testid={`add-sku-${k.replace("_rupees", "").replace("reward_", "").replace("display_", "")}`} value={form[k]}
      onChange={(e) => set(k, e.target.value)} className={`w-full mt-1 px-3 py-2 rounded-lg border ${err[k] ? "border-rose-400" : "border-slate-200"}`} {...rest} />
    {err[k] && <p data-testid={`add-sku-error-${k}`} className="text-[11px] text-rose-600 mt-1">{err[k]}</p>}
  </div>
);

export const AddSkuModal = ({ brands, initialBrand, onClose, onCreated }) => {
  const [form, setForm] = useState({ brand_id: initialBrand || "", name: "", category: "", display_code: "", mrp_rupees: "", reward_points: "" });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErr((e) => ({ ...e, [k]: undefined })); };

  const submit = async () => {
    if (busy) return;
    const v = validateSku(form);
    setErr(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const body = { brand_id: form.brand_id, name: form.name.trim() };
      if (form.category.trim()) body.category = form.category.trim();
      if (form.display_code.trim()) body.display_code = form.display_code.trim().toUpperCase();
      if (form.mrp_rupees !== "") body.mrp_rupees = parseFloat(form.mrp_rupees);
      if (form.reward_points !== "") body.reward_points = parseInt(form.reward_points, 10);
      const { data } = await api.post("/skus", body);
      toast.success(`SKU added as Draft · ${data.display_code}`);
      onCreated();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setErr({ form: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div data-testid="add-sku-modal" className="w-full max-w-md bg-white rounded-xl p-6 fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold font-head">Add SKU</h3><button data-testid="add-sku-close" disabled={busy} onClick={onClose}><X size={18} className="text-slate-400" /></button></div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold text-slate-600">Brand</label>
            <select data-testid="add-sku-brand" value={form.brand_id} onChange={(e) => set("brand_id", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200">
              <option value="">Select brand</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {err.brand_id && <p data-testid="add-sku-error-brand_id" className="text-[11px] text-rose-600 mt-1">{err.brand_id}</p>}
          </div>
          <Field label="SKU name" k="name" form={form} set={set} err={err} placeholder="e.g. Glow Serum 30ml" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category (optional)" k="category" form={form} set={set} err={err} />
            <Field label="SKU code (optional)" k="display_code" form={form} set={set} err={err} placeholder="auto" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="MRP (₹, optional)" k="mrp_rupees" form={form} set={set} err={err} inputMode="decimal" placeholder="e.g. 499" />
            <Field label="Reward pts (optional)" k="reward_points" form={form} set={set} err={err} inputMode="numeric" placeholder="e.g. 20" />
          </div>
          {err.form && <div data-testid="add-sku-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err.form}</div>}
          <p className="text-[11px] text-slate-400">New SKUs are created in Draft and must be activated with an approved reward value before QR use. Duplicate names or codes within a brand are rejected.</p>
          <div className="flex gap-2">
            <button data-testid="add-sku-cancel" disabled={busy} onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 font-semibold py-2.5 rounded-lg disabled:opacity-50">Cancel</button>
            <button data-testid="add-sku-submit" disabled={busy} onClick={submit} className="flex-1 tnp-gradient text-white font-semibold py-2.5 rounded-lg disabled:opacity-60">{busy ? "Adding…" : "Add SKU"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};
